import { QuotationDetailsFragment, ReservedResourceFragment } from 'src/api/api.generated'
import { InMemoryCache } from '@apollo/client'
import { AdditionalService, PriceRange, ResourceAvailabilityRq } from '../api.generated'

export default function createInMemoryCache(): InMemoryCache {
  return new InMemoryCache({
    typePolicies: {
      RqOutput: {
        fields: {
          resources: {
            merge(_existing = [], incoming: ResourceAvailabilityRq[]) {
              return [...(incoming ?? [])]
            },
          },
          rates: {
            merge(_existing = [], incoming: ResourceAvailabilityRq[]) {
              return [...(incoming ?? [])]
            },
          },
        },
      },
      Rate: {
        fields: {
          chargesRate: {
            merge(_existing = [], incoming: ResourceAvailabilityRq[]) {
              return [...(incoming ?? [])]
            },
          },
          priceRanges: {
            merge(_existing = [], incoming?: PriceRange[]) {
              return [...(incoming ?? [])]
            },
          },
          additionalServices: {
            merge(_existing = [], incoming?: AdditionalService[]) {
              return [...(incoming ?? [])]
            },
          },
          childRule: {
            merge(existing, incoming, { mergeObjects }) {
              return mergeObjects(existing, incoming)
            },
          },
        },
      },
      Reservation: {
        fields: {
          reference: {
            merge(existing, incoming, { mergeObjects }) {
              return mergeObjects(existing, incoming)
            },
          },
          origin: {
            merge(existing, incoming, { mergeObjects }) {
              return mergeObjects(existing, incoming)
            },
          },
          reservedResources: {
            merge(existing, incoming) {
              return incoming?.map((i: { id: any }) => {
                const existingItem = existing?.find((e: { id: any }) => e.id === i.id)
                if (existingItem) {
                  return { ...existingItem, ...i }
                }
                return i
              })
            },
          },
        },
      },
      Customer: {
        fields: {
          address: {
            merge(existing, incoming, { mergeObjects }) {
              return mergeObjects(existing, incoming)
            },
          },
          privacy: {
            merge(existing, incoming, { mergeObjects }) {
              return mergeObjects(existing, incoming)
            },
          },
          person: {
            merge(existing, incoming, { mergeObjects }) {
              return mergeObjects(existing, incoming)
            },
          },
          company: {
            merge(existing, incoming, { mergeObjects }) {
              return mergeObjects(existing, incoming)
            },
          },
        },
      },
      Quotation: {
        fields: {
          reference: {
            merge(existing, incoming, { mergeObjects }) {
              return mergeObjects(existing, incoming)
            },
          },
          quotationDetails: {
            merge(_existing = [], incoming?: QuotationDetailsFragment[]) {
              return [...(incoming ?? [])]
            },
          },
          auditable: {
            merge(existing, incoming, { mergeObjects }) {
              return mergeObjects(existing, incoming)
            },
          },
        },
      },
      Property: {
        fields: {
          address: {
            merge(existing, incoming, { mergeObjects }) {
              return mergeObjects(existing, incoming)
            },
          },
        },
      },
      ReservedResource: {
        fields: {
          chargeCategory: {
            merge(existing, incoming, { mergeObjects }) {
              return mergeObjects(existing, incoming)
            },
          },
          policy: {
            merge(existing, incoming, { mergeObjects }) {
              return mergeObjects(existing, incoming)
            },
          },
          productions: {
            merge(existing, incoming) {
              return incoming?.map((i: { id: any }) => {
                const existingItem = existing?.find((e: { id: any }) => e.id === i.id)
                if (existingItem) {
                  return { ...existingItem, ...i }
                }
                return i
              })
            },
          },
          guestOccupancies: {
            merge(existing, incoming) {
              return incoming?.map((i: { id: any }) => {
                const existingItem = existing?.find((e: { id: any }) => e.id === i.id)
                if (existingItem) {
                  return { ...existingItem, ...i }
                }
                return i
              })
            },
          },
          childOccupancies: {
            merge(existing, incoming) {
              return incoming?.map((i: { id: any }) => {
                const existingItem = existing?.find((e: { id: any }) => e.id === i.id)
                if (existingItem) {
                  return { ...existingItem, ...i }
                }
                return i
              })
            },
          },
        },
      },
      ResourceType: {
        fields: {
          name: {
            merge(existing, incoming, { mergeObjects }) {
              return mergeObjects(existing, incoming)
            },
          },
        },
      },
      Document: {
        fields: {
          cashDesk: {
            merge(existing, incoming, { mergeObjects }) {
              return mergeObjects(existing, incoming)
            },
          },
          felAdditionalInfo: {
            merge(existing, incoming, { mergeObjects }) {
              return mergeObjects(existing, incoming)
            },
          },
          customer: {
            merge(existing, incoming, { mergeObjects }) {
              return mergeObjects(existing, incoming)
            },
          },
        },
      },
      ProductionAssigned: {
        fields: {
          production: {
            merge(existing, incoming, { mergeObjects }) {
              return mergeObjects(existing, incoming)
            },
          },
        },
      },
      Production: {
        fields: {
          vat: {
            merge(existing, incoming, { mergeObjects }) {
              return mergeObjects(existing, incoming)
            },
          },
        },
      },
      ResourceHousekeeping: {
        keyFields: ['id', 'reservedResource', ['id']],
      },
    },
  })
}
